import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import {
  WORKFLOW_STEPS,
  getDeepDDTargets,
  hubSpotRequest,
  runWorkflowStep,
  saveDeepDDTargets,
} from "./deepDDApi";
import "./DeepDD.scss";

const defaultProperties = {
  property_report_url: "dociify_report_url",
  property_payload_json: "dociify_payload_json",
  property_investigation_id: "dociify_investigation_id",
  property_last_run_at: "dociify_last_run_at",
  property_risk_level: "dociify_risk_level",
  property_risk_score: "dociify_risk_score",
  property_summary: "dociify_summary",
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const frequencyLabels = {
  immediate: "Run immediately",
  every_5_minutes: "Every 5 minutes",
  every_30_minutes: "Every 30 minutes",
  hourly: "Hourly",
  daily: "Daily",
  weekly: "Weekly",
  manual: "Manual only",
};

function DeepDDSettings() {
  const navigate = useNavigate();
  const toastTimer = useRef(null);
  const [connection, setConnection] = useState({
    label: "HubSpot CRM",
    base_url: "https://api.hubapi.com",
    access_token: "",
  });
  const [apiTargets, setApiTargets] = useState(getDeepDDTargets);
  const [schedule, setSchedule] = useState({
    enabled: false,
    frequency: "every_30_minutes",
    scope: "all",
    processing_rule: "new_accounts",
    query: "",
    limit: 25,
  });
  const [runConfig, setRunConfig] = useState({
    processing_rule: "new_accounts",
    query: "",
    limit: 25,
  });
  const [properties, setProperties] = useState(defaultProperties);
  const [writeBack, setWriteBack] = useState(true);
  const [logs, setLogs] = useState([]);
  const [technicalOutput, setTechnicalOutput] = useState("Ready.");
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [lastScheduledRun, setLastScheduledRun] = useState(null);

  const showToast = (message, type = "success") => {
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    setToast({ message, type });
    toastTimer.current = window.setTimeout(() => setToast(null), 4500);
  };

  const apiConnection = () => ({
    base_url: connection.base_url,
    ...(connection.access_token ? { access_token: connection.access_token } : {}),
  });

  const loadConnection = async () => {
    const data = await hubSpotRequest({ action: "connection_get" });
    if (!data.connection) return;
    setConnection((current) => ({
      ...current,
      label: data.connection.label || "HubSpot CRM",
      base_url: data.connection.base_url || "https://api.hubapi.com",
      access_token: "",
      has_token: data.connection.has_token,
      last_connected_at: data.connection.last_connected_at,
    }));
    const savedSchedule = data.connection.settings?.schedule;
    if (savedSchedule) {
      setSchedule((current) => ({
        ...current,
        ...savedSchedule,
        enabled: Boolean(savedSchedule.enabled),
        frequency: savedSchedule.frequency === "manual"
          ? "every_30_minutes"
          : savedSchedule.frequency,
      }));
    }
    setLastScheduledRun(data.connection.settings?.last_scheduled_run || null);
  };

  const loadLogs = async () => {
    const data = await hubSpotRequest({ action: "run_log_list", limit: 20 });
    setLogs(data.runs || []);
  };

  useEffect(() => {
    Promise.all([loadConnection(), loadLogs()]).catch((error) => showToast(error.message, "error"));
    return () => {
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
    };
    // The initial load should only run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveConnection = async () => {
    setBusy(true);
    try {
      const data = await hubSpotRequest({
        action: "connection_save",
        ...connection,
        settings: { schedule },
      });
      showToast(data.message || "HubSpot connection saved.");
      await loadConnection();
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const saveApiTargets = () => {
    const normalized = saveDeepDDTargets(apiTargets);
    setApiTargets(normalized);
    showToast(`${normalized.environment === "production" ? "Production" : "Test"} API target is now active.`);
  };

  const removeConnection = async () => {
    if (!window.confirm("Remove the saved HubSpot connection?")) return;
    const data = await hubSpotRequest({ action: "connection_remove" });
    showToast(data.message || "HubSpot connection removed.");
    setConnection({ label: "HubSpot CRM", base_url: "https://api.hubapi.com", access_token: "" });
  };

  const testConnection = async () => {
    setBusy(true);
    try {
      const data = await hubSpotRequest({ action: "test", ...apiConnection() });
      setTechnicalOutput(JSON.stringify(data, null, 2));
      showToast(data.message || "HubSpot connection is valid.");
    } catch (error) {
      showToast(error.message, "error");
      setTechnicalOutput(JSON.stringify({ ok: false, error: error.message }, null, 2));
    } finally {
      setBusy(false);
    }
  };

  const saveSchedule = async () => {
    setBusy(true);
    try {
      const data = await hubSpotRequest({
        action: "schedule",
        ...schedule,
        processed_property: properties.property_investigation_id,
      });
      showToast(
        schedule.enabled
          ? `${frequencyLabels[schedule.frequency] || schedule.frequency} schedule is active.`
          : "HubSpot schedule has been disabled."
      );
      setTechnicalOutput(JSON.stringify(data, null, 2));
      await loadConnection();
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const previewAccounts = async () => {
    setBusy(true);
    try {
      const data = await hubSpotRequest({
        action: "run",
        ...apiConnection(),
        scope: "all",
        query: runConfig.query,
        limit: runConfig.limit,
        processing_rule: runConfig.processing_rule,
        processed_property: properties.property_investigation_id,
      });
      setTechnicalOutput(JSON.stringify(data, null, 2));
      showToast(`${data.record_count || 0} account(s) selected.`, "info");
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const workflowPayload = (subject, investigationId, report, risk) => ({
    provider: "dociify",
    investigation_id: investigationId,
    report_url: report.view || report.download || "",
    download_url: report.download || "",
    risk_score: risk.risk_score || null,
    risk_level: risk.risk_level || null,
    subject,
    completed_at: new Date().toISOString(),
  });

  const runEtl = async (processingRule) => {
    if (!writeBack) {
      showToast("Enable HubSpot write-back before running ETL.", "error");
      return;
    }

    setBusy(true);
    let runId = 0;
    let selected = 0;
    let successful = 0;
    let failed = 0;
    const outcomes = [];

    try {
      const started = await hubSpotRequest({
        action: "run_log_start",
        trigger_type: "portal",
        processing_rule: processingRule,
        requested_limit: Number(runConfig.limit),
        details: { query: runConfig.query },
      });
      runId = started.run_id;

      setProgress({ title: "Loading HubSpot accounts", meta: "Applying account rules..." });
      const pull = await hubSpotRequest({
        action: "run",
        ...apiConnection(),
        scope: "all",
        query: runConfig.query,
        limit: runConfig.limit,
        processing_rule: processingRule,
        processed_property: properties.property_investigation_id,
      });
      const records = pull.records || [];
      selected = records.length;

      if (!records.length) {
        await hubSpotRequest({
          action: "run_log_update",
          run_id: runId,
          status: "no_records",
          discovered_count: 0,
          success_count: 0,
          failed_count: 0,
          skipped_count: 0,
        });
        setProgress({ state: "complete", title: "No accounts to process", meta: "No HubSpot contacts matched this rule." });
        await loadLogs();
        return;
      }

      for (let index = 0; index < records.length; index += 1) {
        const subject = records[index];
        let investigationId = "";
        let risk = {};
        let report = {};

        try {
          for (const step of WORKFLOW_STEPS) {
            setProgress({
              title: `Account ${index + 1} of ${records.length}: ${step.label}`,
              meta: `${subject.name || subject.email} | ${successful} successful, ${failed} failed`,
            });
            // eslint-disable-next-line no-await-in-loop
            const result = await runWorkflowStep(step, investigationId, subject);
            if (step.key === "initialize") investigationId = result.investigation_id;
            if (step.key === "risk") risk = result.risk || result || {};
            if (step.key === "report") report = result;
          }

          const payload = workflowPayload(subject, investigationId, report, risk);
          // eslint-disable-next-line no-await-in-loop
          await hubSpotRequest({
            action: "writeback",
            ...apiConnection(),
            ...properties,
            subject,
            workflow_result: {
              investigation_id: investigationId,
              subject,
              report: {
                file: report.file || "",
                view_url: report.view || report.download || "",
                download_url: report.download || "",
              },
              risk,
              payload,
            },
          });
          successful += 1;
          outcomes.push({ name: subject.name, status: "success", investigation_id: investigationId });
        } catch (error) {
          failed += 1;
          outcomes.push({ name: subject.name || subject.email, status: "failed", error: error.message });
        }

        // eslint-disable-next-line no-await-in-loop
        await hubSpotRequest({
          action: "run_log_update",
          run_id: runId,
          status: "running",
          discovered_count: selected,
          success_count: successful,
          failed_count: failed,
          skipped_count: 0,
          details: { outcomes },
        });
      }

      const status = failed ? (successful ? "completed_with_errors" : "failed") : "completed";
      await hubSpotRequest({
        action: "run_log_update",
        run_id: runId,
        status,
        discovered_count: selected,
        success_count: successful,
        failed_count: failed,
        skipped_count: 0,
        details: { outcomes },
      });
      setProgress({
        state: failed ? "error" : "complete",
        title: failed ? "Run completed with errors" : "Run completed",
        meta: `${successful} successful, ${failed} failed, ${selected} selected`,
      });
      setTechnicalOutput(JSON.stringify({ run_id: runId, selected, successful, failed, outcomes }, null, 2));
      await loadLogs();
    } catch (error) {
      setProgress({ state: "error", title: "Run failed", meta: error.message });
      showToast(error.message, "error");
      if (runId) {
        await hubSpotRequest({
          action: "run_log_update",
          run_id: runId,
          status: "failed",
          discovered_count: selected,
          success_count: successful,
          failed_count: Math.max(failed, 1),
          error_message: error.message,
          details: { outcomes },
        }).catch(() => {});
      }
      await loadLogs();
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="page-container deepdd-page deepdd-settings-page">
      <header className="deepdd-header">
        <div>
          <span className="deepdd-eyebrow">3D Intelligence</span>
          <h1>Due Diligence Integration Settings</h1>
          <p>Connect HubSpot, configure account rules, schedule runs, and review operational status.</p>
        </div>
        <button className="deepdd-button secondary" onClick={() => navigate("/DeepDD")}>
          <ArrowBackIcon fontSize="small" /> Investigation
        </button>
      </header>

      {toast && (
        <div className={`deepdd-toast ${toast.type}`} role="status" aria-live="polite">
          <CheckCircleIcon fontSize="small" />
          <span>{toast.message}</span>
          <button type="button" aria-label="Dismiss notification" onClick={() => setToast(null)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
      )}

      <section className="settings-layout">
        <section className="deepdd-card settings-column">
          <div className="card-heading"><div><h2>HubSpot Connection</h2><p>Use a private app token with CRM object permissions.</p></div></div>
          <div className="settings-form">
            <label className="field-label">Connection label<input value={connection.label} onChange={(e) => setConnection({ ...connection, label: e.target.value })} /></label>
            <label className="field-label">HubSpot API base URL<input value={connection.base_url} onChange={(e) => setConnection({ ...connection, base_url: e.target.value })} /></label>
            <label className="field-label">Private app access token<input type="password" value={connection.access_token} placeholder={connection.has_token ? "Saved token available" : "pat-na1-..."} onChange={(e) => setConnection({ ...connection, access_token: e.target.value })} /></label>

            <div className="settings-block">
              <h3>Task Schedule</h3>
              <div className="field-grid">
                <label className="field-label">Status<select value={schedule.enabled ? "active" : "disabled"} onChange={(e) => setSchedule({ ...schedule, enabled: e.target.value === "active" })}><option value="active">Active</option><option value="disabled">Disabled</option></select></label>
                <label className="field-label">Frequency<select value={schedule.frequency} onChange={(e) => setSchedule({ ...schedule, frequency: e.target.value })}><option value="immediate">Run immediately</option><option value="every_5_minutes">Every 5 minutes</option><option value="every_30_minutes">Every 30 minutes</option><option value="hourly">Hourly</option><option value="daily">Daily</option><option value="weekly">Weekly</option></select></label>
                <label className="field-label">Account rule<select value={schedule.processing_rule} onChange={(e) => setSchedule({ ...schedule, processing_rule: e.target.value })}><option value="new_accounts">New accounts only</option><option value="force_all">All accounts (forced)</option></select></label>
                <label className="field-label">Search query<input value={schedule.query} onChange={(e) => setSchedule({ ...schedule, query: e.target.value })} placeholder="Optional search" /></label>
                <label className="field-label">Max records<input type="number" min="1" max="100" value={schedule.limit} onChange={(e) => setSchedule({ ...schedule, limit: e.target.value })} /></label>
              </div>
            </div>

            <div className="button-row">
              <button className="deepdd-button primary" onClick={saveConnection} disabled={busy}><SaveIcon fontSize="small" /> Save</button>
              <button className="deepdd-button secondary" onClick={testConnection} disabled={busy}><CheckCircleIcon fontSize="small" /> Test Token</button>
              <button className="deepdd-button success" onClick={saveSchedule} disabled={busy}>Save Schedule</button>
              <button className="icon-action danger" title="Remove HubSpot" onClick={removeConnection}><DeleteOutlineIcon fontSize="small" /></button>
            </div>
          </div>
        </section>

        <section className="deepdd-card settings-column">
          <div className="card-heading"><div><h2>HubSpot ETL</h2><p>Preview accounts or run the complete due diligence workflow.</p></div></div>
          <div className="settings-form">
            <div className="field-grid">
              <label className="field-label">Account rule<select value={runConfig.processing_rule} onChange={(e) => setRunConfig({ ...runConfig, processing_rule: e.target.value })}><option value="new_accounts">New accounts only</option><option value="force_all">All accounts (forced)</option></select></label>
              <label className="field-label">Max records<input type="number" min="1" max="100" value={runConfig.limit} onChange={(e) => setRunConfig({ ...runConfig, limit: e.target.value })} /></label>
            </div>
            <label className="field-label">Optional search<input value={runConfig.query} onChange={(e) => setRunConfig({ ...runConfig, query: e.target.value })} placeholder="Email, company, domain, or contact name" /></label>
            <button className="deepdd-button secondary preview-button" onClick={previewAccounts} disabled={busy}>Preview Accounts</button>

            <label className="writeback-control">
              <input type="checkbox" checked={writeBack} onChange={(e) => setWriteBack(e.target.checked)} />
              <span><strong>Write results back to HubSpot</strong><small>Required to mark completed contacts and prevent duplicate processing.</small></span>
            </label>

            <details className="property-mapping">
              <summary>HubSpot property mapping</summary>
              <div className="field-grid">
                {Object.entries(properties).map(([key, value]) => (
                  <label className="field-label" key={key}>
                    {key.replace("property_", "").replaceAll("_", " ")}
                    <input value={value} onChange={(e) => setProperties({ ...properties, [key]: e.target.value })} />
                  </label>
                ))}
              </div>
            </details>

            <div className="etl-rule-note">New Accounts skips contacts with a Dociify ID. Force Run All deliberately reprocesses every matching contact.</div>
            <div className="etl-actions portal-etl-actions">
              <button className="deepdd-button success" disabled={busy || !writeBack} onClick={() => runEtl("new_accounts")}><PlayArrowIcon fontSize="small" /> Run New Accounts</button>
              <button className="deepdd-button primary" disabled={busy || !writeBack} onClick={() => runEtl("force_all")}><PlayArrowIcon fontSize="small" /> Force Run All</button>
            </div>

            {progress && (
              <div className={`portal-progress ${progress.state || ""}`}>
                {!progress.state && <span className="portal-spinner" />}
                <span><strong>{progress.title}</strong><small>{progress.meta}</small></span>
              </div>
            )}

            <details className="technical-output">
              <summary>Technical output</summary>
              <pre>{technicalOutput}</pre>
            </details>
          </div>
        </section>
      </section>

      <section className="deepdd-card portal-log-card">
        <div className="card-heading">
          <div><h2>DeepDD API Target</h2><p>Set the shared backend location used by the Investigation and Settings screens.</p></div>
          <span className={`environment-badge ${apiTargets.environment}`}>{apiTargets.environment === "production" ? "Production" : "Test"}</span>
        </div>
        <div className="settings-form api-target-form">
          <div className="field-grid api-target-grid">
            <label className="field-label">Active environment<select value={apiTargets.environment} onChange={(e) => setApiTargets({ ...apiTargets, environment: e.target.value })}><option value="test">Test</option><option value="production">Production</option></select></label>
            <label className="field-label">Test API location<input value={apiTargets.test_url} onChange={(e) => setApiTargets({ ...apiTargets, test_url: e.target.value })} placeholder="https://dev2.4pay.ca/portal/www/3D/business" /></label>
            <label className="field-label">Production API location<input value={apiTargets.production_url} onChange={(e) => setApiTargets({ ...apiTargets, production_url: e.target.value })} placeholder="https://dev2.4pay.ca/dociify3" /></label>
          </div>
          <div className="api-target-actions">
            <p>Changes apply immediately to all DeepDD API calls in this browser. No Portal rebuild is required.</p>
            <button className="deepdd-button primary" type="button" onClick={saveApiTargets}><SaveIcon fontSize="small" /> Save API Target</button>
          </div>
        </div>
      </section>

      <section className="deepdd-card portal-log-card">
        <div className="card-heading">
          <div><h2>Automation Schedule</h2><p>Current HubSpot processing cadence and execution status.</p></div>
        </div>
        <div className="history-table-wrap">
          <table>
            <thead><tr><th>Integration</th><th>Frequency</th><th>Account rule</th><th>Status</th><th>Last run</th><th>Last changed</th></tr></thead>
            <tbody>
              <tr>
                <td><strong>{connection.label || "HubSpot CRM"}</strong></td>
                <td>{frequencyLabels[schedule.frequency] || schedule.frequency}</td>
                <td>{schedule.processing_rule === "force_all" ? "All accounts (forced)" : "New accounts only"}</td>
                <td><span className={`schedule-status ${schedule.enabled ? "active" : "disabled"}`}>{schedule.enabled ? "Active" : "Disabled"}</span></td>
                <td>{formatDate(lastScheduledRun?.ran_at || logs.find((run) => run.trigger_type === "scheduled")?.started_at)}</td>
                <td>{formatDate(schedule.updated_at)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="deepdd-card portal-log-card">
        <div className="card-heading">
          <div><h2>HubSpot Run Log</h2><p>Processing outcomes from portal and scheduled runs.</p></div>
          <button className="icon-action" title="Refresh logs" onClick={loadLogs}><RefreshIcon fontSize="small" /></button>
        </div>
        <div className="history-table-wrap">
          <table>
            <thead><tr><th>Started</th><th>Trigger</th><th>Rule</th><th>Status</th><th>Selected</th><th>Successful</th><th>Failed</th><th>Completed</th></tr></thead>
            <tbody>
              {logs.map((run) => (
                <tr key={run.id}>
                  <td>{formatDate(run.started_at)}</td>
                  <td>{run.trigger_type}</td>
                  <td>{run.processing_rule === "force_all" ? "All accounts (forced)" : "New accounts"}</td>
                  <td><span className={`run-log-status ${run.status}`}>{run.status.replaceAll("_", " ")}</span></td>
                  <td>{run.discovered_count}</td>
                  <td className="success-count">{run.success_count}</td>
                  <td className="failure-count">{run.failed_count}</td>
                  <td>{formatDate(run.completed_at)}</td>
                </tr>
              ))}
              {!logs.length && <tr><td colSpan="8">No HubSpot runs have been logged yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

export default DeepDDSettings;
